import React, { useState } from 'react';
import { BuildingOffice2Icon, ChevronDownIcon } from '@heroicons/react/24/outline';
import {
  COMPANY_PROFILE_FIELD_LABELS,
  COMPANY_SERVICE_TYPE_LABELS,
} from '@claimup/shared/constants/company';

/**
 * En nombre de quién llamas, y **qué puedes ofrecerle al deudor**.
 *
 * La otra mitad del briefing. `DebtorBriefing` dice a quién llamas; esto, con qué
 * autoridad. El gestor trabaja carteras de varios clientes seguidas y no puede
 * salir de la llamada a comprobar si este admite una quita: con ~117 s de
 * conversación, ir a otra pantalla es salir de la conversación. Y la pregunta llega
 * siempre, porque la hace el deudor — «¿me lo dejáis en menos?», «¿puedo pagarlo a
 * plazos?», «¿dónde lo pago?».
 *
 * ## 🔴 El panel tiene altura FIJA, y es el punto del diseño
 *
 * La primera versión pintaba cada campo entero y crecía con el texto. Con datos
 * reales se vio el fallo el 29/09/2026: la política de planes de pago de Access
 * Finance son ~350 palabras, más que todo lo demás junto. Como el panel comparte
 * fila con la ficha del deudor, **el alto del panel ES el alto de la fila**: el
 * expediente entero se iba casi una pantalla hacia abajo, y el IBAN quedaba fuera
 * de vista. Lo pidió Edgar el mismo día: «¿puedes hacer que la info del expediente
 * no baje?».
 *
 * Error de medición: se contó cuántos clientes tenían cada campo, no **cuánto
 * ocupaban**. Un campo de tres líneas y uno de treinta no se comportan igual en
 * pantalla, y eso no se deduce del código.
 *
 * Así que el panel se reparte en tres, y **solo el de en medio scrollea**:
 *
 *   ┌──────────────────────────┐
 *   │ de parte de quién llamas │  fijo  — nunca se va de vista
 *   ├──────────────────────────┤
 *   │ qué puedes ofrecer       │  scroll — las políticas largas viven aquí
 *   │ disputas y contexto      │
 *   ├──────────────────────────┤
 *   │ dónde paga               │  fijo  — es el final de toda llamada que va bien
 *   └──────────────────────────┘
 *
 * De ahí que «Dónde paga» esté al final del fichero y no junto a las políticas: no
 * es el orden de lectura, es que **está anclado**. Moverlo dentro del bloque que
 * scrollea devuelve el problema: el IBAN se va de pantalla justo cuando el deudor
 * dice que sí. Hay test.
 */

/** Lo que se puede ofrecer. Es lo que pregunta el deudor, así que va lo primero. */
const NEGOCIACION = ['discount_policy', 'payment_plan_policy'];

/** Dónde paga. Corto y crítico: va anclado abajo, fuera del scroll. */
const COBRO = ['payment_link', 'iban'];

/** Para cuando se resiste. Plegado, pero a un clic. */
const RESISTENCIA = ['frequent_disputes', 'non_payment_consequences'];

/** Contexto. Se lee antes de empezar, no en mitad de una llamada. */
const CONTEXTO = ['description', 'debtor_profile', 'ar_management_time', 'contact_names'];

/**
 * A partir de aquí un campo se recorta, con un «ver más» propio.
 *
 * No es un `line-clamp` de CSS a propósito: el recorte en CSS **no se puede
 * comprobar en un test** —jsdom no maqueta, así que un texto recortado y uno entero
 * son idénticos para cualquier aserción—. Siendo un número, se lee y se prueba.
 */
const LIMITE = 180;

/** Recorta sin partir una palabra por la mitad. */
const recortar = (texto) => {
  if (texto.length <= LIMITE) return texto;
  const corte = texto.slice(0, LIMITE);
  const espacio = corte.lastIndexOf(' ');
  return `${corte.slice(0, espacio > LIMITE * 0.6 ? espacio : LIMITE).trimEnd()}…`;
};

const Campo = ({ campo, valor, destacado = false }) => {
  const [entero, setEntero] = useState(false);
  const largo = valor.length > LIMITE;

  return (
    <div className={destacado ? 'rounded-lg bg-white p-2.5 ring-1 ring-gray-200' : 'py-1.5'}>
      <p className='text-xs font-medium uppercase tracking-wide text-gray-500'>
        {COMPANY_PROFILE_FIELD_LABELS[campo] || campo}
      </p>
      {/*
        `whitespace-pre-line`: son campos de texto libre y el cliente escribe las
        políticas como una lista con saltos de línea. Sin esto, una política de
        plazos de cuatro puntos se lee como un párrafo corrido — justo cuando hay
        que leerla de un vistazo.
      */}
      <p className='mt-0.5 whitespace-pre-line break-words text-sm text-gray-800'>
        {entero || !largo ? valor : recortar(valor)}
      </p>
      {largo && (
        <button
          type='button'
          onClick={() => setEntero((v) => !v)}
          className='mt-1 text-xs font-medium text-blue-600 hover:underline'
        >
          {entero ? 'Ver menos' : 'Ver más'}
        </button>
      )}
    </div>
  );
};

const CompanyProfilePanel = ({ company }) => {
  const [abierto, setAbierto] = useState(false);

  if (!company) return null;

  const perfil = company.profile || {};
  const conValor = (campos) => campos.filter((campo) => perfil[campo]);

  const negociacion = conValor(NEGOCIACION);
  const cobro = conValor(COBRO);
  const resto = [...conValor(RESISTENCIA), ...conValor(CONTEXTO)];

  return (
    /*
      El tope de altura va en `lg:` porque en pantalla estrecha no hay fila —los
      bloques se apilan y no empujan nada de lado—, y ahí limitar la altura solo
      escondería contenido sin ganar nada.
    */
    <div className='flex flex-col rounded-lg border border-gray-200 bg-gray-50 lg:max-h-[28rem]'>
      <div className='flex shrink-0 items-center gap-2 border-b border-gray-200 px-4 py-3'>
        <BuildingOffice2Icon className='h-5 w-5 shrink-0 text-gray-400' aria-hidden='true' />
        <div className='min-w-0'>
          {/*
            Lo primero que se lee: de parte de quién llamas. Decir el cliente
            equivocado no tiene arreglo dentro de la conversación.
          */}
          <p className='truncate font-semibold text-gray-900'>{company.name || 'Cliente'}</p>
          {company.serviceType && (
            <p className='text-xs text-gray-500'>
              {COMPANY_SERVICE_TYPE_LABELS[company.serviceType] || company.serviceType}
            </p>
          )}
        </div>
      </div>

      {/*
        La única parte que scrollea. `min-h-0` es OBLIGATORIO: sin él, un hijo de un
        flex no encoge por debajo de su contenido, el div crece igual y el tope de
        altura de arriba no sirve de nada — el panel volvería a empujar el
        expediente, y sin que nada lo delate.
      */}
      <div data-testid='perfil-scroll' className='min-h-0 space-y-3 overflow-y-auto p-4'>
        {negociacion.length > 0 && (
          <div className='space-y-2'>
            <p className='text-xs font-semibold uppercase tracking-wide text-emerald-700'>
              Qué puedes ofrecer
            </p>
            {negociacion.map((campo) => (
              <Campo key={campo} campo={campo} valor={perfil[campo]} destacado />
            ))}
          </div>
        )}

        {negociacion.length === 0 && (
          /*
            Decirlo, en vez de no pintar nada.
            =================================
            🔴 La condición es que falte LA POLÍTICA, no que el panel esté vacío del
            todo. Medido en producción el 29/09/2026: de los seis clientes a los que
            se está llamando, **dos tienen solo el IBAN**. Con esos dos, la versión
            anterior de este bloque no decía nada —había algo en «Dónde paga», así
            que se callaba— y el gestor veía un panel con un IBAN y ningún límite de
            negociación. Un bloque ausente se lee como «no hay política», que es
            justo la lectura que acaba en una quita prometida que el cliente no
            acepta. No tener política configurada ES información, y hay que decirla.
          */
          <p className='text-sm text-gray-500'>
            Este cliente no tiene configurada política de quitas ni de plazos.{' '}
            <span className='font-medium text-gray-700'>
              No prometas condiciones sin confirmarlas.
            </span>
          </p>
        )}

        {resto.length > 0 && (
          <div className='border-t border-gray-200 pt-1'>
            <button
              type='button'
              onClick={() => setAbierto((v) => !v)}
              className='flex w-full items-center justify-between rounded-lg py-1.5 text-sm font-medium text-gray-600 hover:text-gray-900'
            >
              <span>{abierto ? 'Ocultar el resto' : 'Disputas, consecuencias y contexto'}</span>
              <ChevronDownIcon
                className={`h-4 w-4 transition-transform ${abierto ? 'rotate-180' : ''}`}
                aria-hidden='true'
              />
            </button>

            {abierto && (
              <div className='divide-y divide-gray-200'>
                {resto.map((campo) => (
                  <Campo key={campo} campo={campo} valor={perfil[campo]} />
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/*
        🔴 ANCLADO, fuera del scroll. Es lo que hace falta al final de toda llamada
        que va bien, y con la política de plazos de un cliente largo el IBAN se iba
        de pantalla. Pedir el número de cuenta dos veces es de las pocas cosas que
        hacen perder una conversación ya ganada.
      */}
      {cobro.length > 0 && (
        <div
          data-testid='perfil-cobro'
          className='shrink-0 space-y-2 border-t border-gray-200 bg-gray-100 p-4'
        >
          <p className='text-xs font-semibold uppercase tracking-wide text-blue-700'>Dónde paga</p>
          {cobro.map((campo) => (
            <Campo key={campo} campo={campo} valor={perfil[campo]} destacado />
          ))}
        </div>
      )}
    </div>
  );
};

export default CompanyProfilePanel;
